import React from 'react';

/**
 * Product Schema Markup Generator
 * Generates JSON-LD structured data for products to improve SEO
 */

export interface ProductSchemaData {
  name: string;
  description: string;
  image: string | string[];
  sku: string;
  brand?: string;
  price: number;
  priceCurrency?: string;
  availability?: 'InStock' | 'OutOfStock' | 'PreOrder' | 'Discontinued';
  url: string;
  category?: string;
  rating?: {
    ratingValue: number;
    reviewCount: number;
    bestRating?: number;
    worstRating?: number;
  };
  offers?: {
    price: number;
    priceCurrency: string;
    availability: string;
    validFrom?: string;
    validThrough?: string;
    priceValidUntil?: string;
  };
}

export function generateProductSchema(data: ProductSchemaData) {
  const schema: any = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: data.name,
    description: data.description,
    image: Array.isArray(data.image) ? data.image : [data.image],
    sku: data.sku,
    brand: {
      '@type': 'Brand',
      name: data.brand || 'Smart Blinds™ Hub'
    },
    offers: {
      '@type': 'Offer',
      url: data.url,
      priceCurrency: data.priceCurrency || 'USD',
      price: (typeof data.price === 'number' ? data.price : parseFloat(data.price) || 0).toFixed(2),
      availability: `https://schema.org/${data.availability || 'InStock'}`,
      ...(data.offers?.validFrom && { validFrom: data.offers.validFrom }),
      ...(data.offers?.validThrough && { validThrough: data.offers.validThrough }),
      ...(data.offers?.priceValidUntil && { priceValidUntil: data.offers.priceValidUntil }),
    },
  };

  // Add category if provided
  if (data.category) {
    schema.category = data.category;
  }

  // Add aggregateRating ONLY for products with real reviews. Emitting star
  // markup on a review-less product risks a Google manual action, so require a
  // positive rating AND at least one review (reviewCount can arrive as a string
  // from MySQL, so coerce before comparing).
  const ratingValueNum = typeof data.rating?.ratingValue === 'number'
    ? data.rating.ratingValue : parseFloat(String(data.rating?.ratingValue)) || 0;
  const reviewCountNum = typeof data.rating?.reviewCount === 'number'
    ? data.rating.reviewCount : parseInt(String(data.rating?.reviewCount)) || 0;
  if (ratingValueNum > 0 && reviewCountNum > 0) {
    schema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: ratingValueNum.toFixed(1),
      reviewCount: reviewCountNum,
      bestRating: data.rating.bestRating || 5,
      worstRating: data.rating.worstRating || 1,
    };
  }

  return schema;
}

/**
 * Generate Product JSON-LD script tag
 */
export function ProductSchemaScript({ data }: { data: ProductSchemaData }) {
  const schema = generateProductSchema(data);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
